// The entry flows: sign-up, log-in, verification, recovery and invitation (C-14).
//
// Namespace `entry`; owner: the polish onboarding chunk (PL-04, PL-25). Field
// labels, helper text, per-field errors, busy labels and the email-sent state.
// Server codes map to keys in ui/components/auth/entry-copy.ts; codes that
// already have 24-language copy stay in ui/components/auth/strings.ts.
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  signupTitle: 'Set up your company',
  signupLead:
    'Sign up with your work email. You become the owner, and teammates join by invitation.',
  loginTitle: 'Log in to Poneva',
  inviteTitle: 'Join {company}',
  forgotTitle: 'Reset your password',
  forgotLead: 'Enter your email and we will send you a one\u2011time link.',
  resetTitle: 'Choose a new password',
  resetLead: 'The link works once and expires 30 minutes after it was sent.',
  resetDoneTitle: 'Password changed',
  resetDoneLead: 'You were logged out everywhere. Log in with your new password.',
  companyLabel: 'Company name',
  workEmailLabel: 'Work email',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  newPasswordLabel: 'New password',
  confirmPasswordLabel: 'Confirm new password',
  passwordHelp: 'At least 15 characters',
  signingUpAs: 'Signing up as {email}',
  forgotPassword: 'Forgot password?',
  sendResetLink: 'Send reset link',
  saveNewPassword: 'Save new password',
  continueToLogin: 'Continue to log in',
  creatingAccount: 'Creating account…',
  loggingIn: 'Logging in…',
  sending: 'Sending…',
  saving: 'Saving…',
  confirming: 'Confirming…',
  companyRequired: 'Enter your company name.',
  companyTooLong: 'Keep the company name to 120 characters or fewer.',
  emailRequired: 'Enter your email address.',
  emailInvalid: 'Enter a valid email address, like name@company.com.',
  workEmailRequired: 'Use your work email. Invited? Open your invitation link instead.',
  passwordRequired: 'Enter your password.',
  passwordTooShort: 'Use at least 15 characters.',
  passwordTooLong: 'The password is too long.',
  passwordCompromised: 'This password appears in known data breaches. Choose another one.',
  passwordMismatch: 'The passwords do not match.',
  resetExpired: 'This link is invalid, expired or already used. Request a new one.',
  checkInboxTitle: 'Check your inbox',
  sentTo: 'We sent a link to',
  signupSentHint: 'Open it on any device to finish setting up your company.',
  inviteSentHint: 'Open it on any device to finish joining.',
  resetSentTo: 'If there’s a Poneva account for',
  resetSentHint: 'a reset link is on its way. Check your spam folder too.',
  resend: 'Resend email',
  resendIn: 'Resend in {seconds} s',
  resent: 'Sent again. Check your inbox.',
  resendFailed: 'We could not send it again. Try again in a moment.',
  signupExpired: 'This sign-up has expired. Start again with your email.',
  useAnotherEmail: 'Use another email',
  checkingInvite: 'Checking your invitation…',
  inviteUnavailableTitle: 'We could not check this invitation',
  inviteUnavailableLead: 'Check your connection and try again.',
  tryAgain: 'Try again',
  inviteInvalidTitle: 'This invitation can’t be used',
  inviteInvalidLead:
    'It may have expired or already been used. Ask the person who invited you for a new link.',
  createCompany: 'Create a company',
  backToInvite: 'Back to your invitation',
};

export type EntryCopyKey = keyof typeof en;
export type EntryCopy = Readonly<Record<EntryCopyKey, string>>;

const bg: EntryCopy = {
  signupTitle: 'Регистрирайте компанията си',
  signupLead:
    'Регистрирайте се със служебния си имейл. Вие ставате собственик, а колегите се присъединяват с покана.',
  loginTitle: 'Вход в Poneva',
  inviteTitle: 'Присъединете се към {company}',
  forgotTitle: 'Нулиране на паролата',
  forgotLead: 'Въведете имейла си и ще ви изпратим еднократна връзка.',
  resetTitle: 'Изберете нова парола',
  resetLead: 'Връзката работи веднъж и изтича 30 минути след изпращането си.',
  resetDoneTitle: 'Паролата е сменена',
  resetDoneLead: 'Излязохте от всички устройства. Влезте с новата си парола.',
  companyLabel: 'Име на компанията',
  workEmailLabel: 'Служебен имейл',
  emailLabel: 'Имейл',
  passwordLabel: 'Парола',
  newPasswordLabel: 'Нова парола',
  confirmPasswordLabel: 'Потвърдете новата парола',
  passwordHelp: 'Поне 15 знака',
  signingUpAs: 'Регистрирате се като {email}',
  forgotPassword: 'Забравена парола?',
  sendResetLink: 'Изпращане на връзка за нулиране',
  saveNewPassword: 'Запазване на новата парола',
  continueToLogin: 'Към вход',
  creatingAccount: 'Създаване на профил…',
  loggingIn: 'Влизане…',
  sending: 'Изпращане…',
  saving: 'Запазване…',
  confirming: 'Потвърждаване…',
  companyRequired: 'Въведете името на компанията.',
  companyTooLong: 'Името на компанията може да е до 120 знака.',
  emailRequired: 'Въведете имейл адреса си.',
  emailInvalid: 'Въведете валиден имейл адрес, например name@company.com.',
  workEmailRequired: 'Използвайте служебния си имейл. Имате покана? Отворете връзката от поканата.',
  passwordRequired: 'Въведете паролата си.',
  passwordTooShort: 'Използвайте поне 15 знака.',
  passwordTooLong: 'Паролата е твърде дълга.',
  passwordCompromised: 'Тази парола се среща в известни изтичания на данни. Изберете друга.',
  passwordMismatch: 'Паролите не съвпадат.',
  resetExpired: 'Връзката е невалидна, изтекла или вече използвана. Поискайте нова.',
  checkInboxTitle: 'Проверете пощата си',
  sentTo: 'Изпратихме връзка до',
  signupSentHint:
    'Отворете я на което и да е устройство, за да завършите настройката на компанията.',
  inviteSentHint: 'Отворете я на което и да е устройство, за да се присъедините.',
  resetSentTo: 'Ако има профил в Poneva за',
  resetSentHint: 'връзка за нулиране вече е на път. Проверете и папката за спам.',
  resend: 'Изпрати отново',
  resendIn: 'Изпращане отново след {seconds} с',
  resent: 'Изпратено отново. Проверете пощата си.',
  resendFailed: 'Не успяхме да изпратим отново. Опитайте след малко.',
  signupExpired: 'Регистрацията е изтекла. Започнете отначало с имейла си.',
  useAnotherEmail: 'Използвайте друг имейл',
  checkingInvite: 'Проверяваме поканата ви…',
  inviteUnavailableTitle: 'Не успяхме да проверим поканата',
  inviteUnavailableLead: 'Проверете връзката си и опитайте отново.',
  tryAgain: 'Опитайте отново',
  inviteInvalidTitle: 'Тази покана не може да се използва',
  inviteInvalidLead:
    'Възможно е да е изтекла или вече да е използвана. Помолете човека, който ви е поканил, за нова връзка.',
  createCompany: 'Създайте компания',
  backToInvite: 'Обратно към поканата',
};

const hr: EntryCopy = {
  signupTitle: 'Postavite svoju tvrtku',
  signupLead:
    'Registrirajte se poslovnom e-poštom. Postajete vlasnik, a kolege se pridružuju putem pozivnice.',
  loginTitle: 'Prijavite se u Poneva',
  inviteTitle: 'Pridružite se tvrtki {company}',
  forgotTitle: 'Resetirajte lozinku',
  forgotLead: 'Unesite svoju e-poštu i poslat ćemo vam jednokratnu poveznicu.',
  resetTitle: 'Odaberite novu lozinku',
  resetLead: 'Poveznica radi jednom i istječe 30 minuta nakon slanja.',
  resetDoneTitle: 'Lozinka je promijenjena',
  resetDoneLead: 'Odjavljeni ste sa svih uređaja. Prijavite se novom lozinkom.',
  companyLabel: 'Naziv tvrtke',
  workEmailLabel: 'Poslovna e-pošta',
  emailLabel: 'E-pošta',
  passwordLabel: 'Lozinka',
  newPasswordLabel: 'Nova lozinka',
  confirmPasswordLabel: 'Potvrdite novu lozinku',
  passwordHelp: 'Najmanje 15 znakova',
  signingUpAs: 'Registrirate se kao {email}',
  forgotPassword: 'Zaboravili ste lozinku?',
  sendResetLink: 'Pošalji poveznicu za resetiranje',
  saveNewPassword: 'Spremi novu lozinku',
  continueToLogin: 'Nastavi na prijavu',
  creatingAccount: 'Stvaranje računa…',
  loggingIn: 'Prijava…',
  sending: 'Slanje…',
  saving: 'Spremanje…',
  confirming: 'Potvrđivanje…',
  companyRequired: 'Unesite naziv tvrtke.',
  companyTooLong: 'Naziv tvrtke može imati najviše 120 znakova.',
  emailRequired: 'Unesite adresu e-pošte.',
  emailInvalid: 'Unesite valjanu adresu e-pošte, npr. ime@tvrtka.hr.',
  workEmailRequired:
    'Koristite poslovnu e-poštu. Imate pozivnicu? Otvorite poveznicu iz pozivnice.',
  passwordRequired: 'Unesite lozinku.',
  passwordTooShort: 'Upotrijebite najmanje 15 znakova.',
  passwordTooLong: 'Lozinka je preduga.',
  passwordCompromised: 'Ova se lozinka pojavljuje u poznatim curenjima podataka. Odaberite drugu.',
  passwordMismatch: 'Lozinke se ne podudaraju.',
  resetExpired: 'Poveznica nije valjana, istekla je ili je već iskorištena. Zatražite novu.',
  checkInboxTitle: 'Provjerite pristiglu poštu',
  sentTo: 'Poslali smo poveznicu na',
  signupSentHint: 'Otvorite je na bilo kojem uređaju kako biste dovršili postavljanje tvrtke.',
  inviteSentHint: 'Otvorite je na bilo kojem uređaju kako biste se pridružili.',
  resetSentTo: 'Ako postoji Poneva račun za',
  resetSentHint: 'poveznica za ponovno postavljanje je na putu. Provjerite i mapu neželjene pošte.',
  resend: 'Pošalji ponovno',
  resendIn: 'Ponovno slanje za {seconds} s',
  resent: 'Ponovno poslano. Provjerite pristiglu poštu.',
  resendFailed: 'Nismo je mogli ponovno poslati. Pokušajte ponovno za trenutak.',
  signupExpired: 'Registracija je istekla. Počnite ispočetka sa svojom e-poštom.',
  useAnotherEmail: 'Upotrijebite drugu e-poštu',
  checkingInvite: 'Provjeravamo vašu pozivnicu…',
  inviteUnavailableTitle: 'Nismo mogli provjeriti ovu pozivnicu',
  inviteUnavailableLead: 'Provjerite vezu i pokušajte ponovno.',
  tryAgain: 'Pokušaj ponovno',
  inviteInvalidTitle: 'Ova se pozivnica ne može upotrijebiti',
  inviteInvalidLead:
    'Možda je istekla ili je već iskorištena. Zamolite osobu koja vas je pozvala za novu poveznicu.',
  createCompany: 'Stvorite tvrtku',
  backToInvite: 'Natrag na pozivnicu',
};

const cs: EntryCopy = {
  signupTitle: 'Nastavte svou firmu',
  signupLead:
    'Zaregistrujte se pracovním e-mailem. Stanete se vlastníkem a kolegové se připojí na pozvánku.',
  loginTitle: 'Přihlásit se do Poneva',
  inviteTitle: 'Připojte se k {company}',
  forgotTitle: 'Obnovte heslo',
  forgotLead: 'Zadejte svůj e-mail a pošleme vám jednorázový odkaz.',
  resetTitle: 'Zvolte nové heslo',
  resetLead: 'Odkaz funguje jednou a vyprší 30 minut po odeslání.',
  resetDoneTitle: 'Heslo bylo změněno',
  resetDoneLead: 'Byli jste všude odhlášeni. Přihlaste se novým heslem.',
  companyLabel: 'Název firmy',
  workEmailLabel: 'Pracovní e-mail',
  emailLabel: 'E-mail',
  passwordLabel: 'Heslo',
  newPasswordLabel: 'Nové heslo',
  confirmPasswordLabel: 'Potvrďte nové heslo',
  passwordHelp: 'Alespoň 15 znaků',
  signingUpAs: 'Registrujete se jako {email}',
  forgotPassword: 'Zapomněli jste heslo?',
  sendResetLink: 'Odeslat odkaz pro obnovení',
  saveNewPassword: 'Uložit nové heslo',
  continueToLogin: 'Pokračovat k přihlášení',
  creatingAccount: 'Vytváření účtu…',
  loggingIn: 'Přihlašování…',
  sending: 'Odesílání…',
  saving: 'Ukládání…',
  confirming: 'Potvrzování…',
  companyRequired: 'Zadejte název firmy.',
  companyTooLong: 'Název firmy může mít nejvýše 120 znaků.',
  emailRequired: 'Zadejte svou e-mailovou adresu.',
  emailInvalid: 'Zadejte platnou e-mailovou adresu, například jmeno@firma.cz.',
  workEmailRequired: 'Použijte pracovní e-mail. Máte pozvánku? Otevřete raději odkaz z pozvánky.',
  passwordRequired: 'Zadejte heslo.',
  passwordTooShort: 'Použijte alespoň 15 znaků.',
  passwordTooLong: 'Heslo je příliš dlouhé.',
  passwordCompromised: 'Toto heslo se objevuje ve známých únicích dat. Zvolte jiné.',
  passwordMismatch: 'Hesla se neshodují.',
  resetExpired: 'Tento odkaz je neplatný, vypršel nebo už byl použit. Požádejte o nový.',
  checkInboxTitle: 'Zkontrolujte svou schránku',
  sentTo: 'Poslali jsme odkaz na adresu',
  signupSentHint: 'Otevřete ho na libovolném zařízení a dokončete nastavení firmy.',
  inviteSentHint: 'Otevřete ho na libovolném zařízení a dokončete připojení.',
  resetSentTo: 'Pokud existuje účet Poneva pro',
  resetSentHint: 'odkaz pro obnovení je na cestě. Zkontrolujte i složku se spamem.',
  resend: 'Odeslat znovu',
  resendIn: 'Odeslat znovu za {seconds} s',
  resent: 'Odesláno znovu. Zkontrolujte svou schránku.',
  resendFailed: 'Nepodařilo se odeslat znovu. Zkuste to za chvíli.',
  signupExpired: 'Tato registrace vypršela. Začněte znovu se svým e-mailem.',
  useAnotherEmail: 'Použít jiný e-mail',
  checkingInvite: 'Kontrolujeme vaši pozvánku…',
  inviteUnavailableTitle: 'Pozvánku se nepodařilo zkontrolovat',
  inviteUnavailableLead: 'Zkontrolujte připojení a zkuste to znovu.',
  tryAgain: 'Zkusit znovu',
  inviteInvalidTitle: 'Tuto pozvánku nelze použít',
  inviteInvalidLead:
    'Mohla vypršet nebo už byla použita. Požádejte člověka, který vás pozval, o nový odkaz.',
  createCompany: 'Vytvořit firmu',
  backToInvite: 'Zpět k pozvánce',
};

const da: EntryCopy = {
  signupTitle: 'Kom i gang med din virksomhed',
  signupLead:
    'Tilmeld dig med din arbejdsmail. Du bliver ejer, og kolleger kommer med via invitation.',
  loginTitle: 'Log ind på Poneva',
  inviteTitle: 'Bliv en del af {company}',
  forgotTitle: 'Nulstil din adgangskode',
  forgotLead: 'Indtast din e-mail, så sender vi dig et engangslink.',
  resetTitle: 'Vælg en ny adgangskode',
  resetLead: 'Linket virker én gang og udløber 30 minutter efter, det blev sendt.',
  resetDoneTitle: 'Adgangskoden er ændret',
  resetDoneLead: 'Du er logget ud overalt. Log ind med din nye adgangskode.',
  companyLabel: 'Virksomhedens navn',
  workEmailLabel: 'Arbejdsmail',
  emailLabel: 'E-mail',
  passwordLabel: 'Adgangskode',
  newPasswordLabel: 'Ny adgangskode',
  confirmPasswordLabel: 'Bekræft ny adgangskode',
  passwordHelp: 'Mindst 15 tegn',
  signingUpAs: 'Tilmelder som {email}',
  forgotPassword: 'Glemt adgangskode?',
  sendResetLink: 'Send nulstillingslink',
  saveNewPassword: 'Gem ny adgangskode',
  continueToLogin: 'Fortsæt til login',
  creatingAccount: 'Opretter konto…',
  loggingIn: 'Logger ind…',
  sending: 'Sender…',
  saving: 'Gemmer…',
  confirming: 'Bekræfter…',
  companyRequired: 'Indtast virksomhedens navn.',
  companyTooLong: 'Virksomhedsnavnet må højst være på 120 tegn.',
  emailRequired: 'Indtast din e-mailadresse.',
  emailInvalid: 'Indtast en gyldig e-mailadresse, fx navn@virksomhed.dk.',
  workEmailRequired: 'Brug din arbejdsmail. Er du inviteret? Åbn dit invitationslink i stedet.',
  passwordRequired: 'Indtast din adgangskode.',
  passwordTooShort: 'Brug mindst 15 tegn.',
  passwordTooLong: 'Adgangskoden er for lang.',
  passwordCompromised: 'Adgangskoden findes i kendte datalæk. Vælg en anden.',
  passwordMismatch: 'Adgangskoderne er ikke ens.',
  resetExpired: 'Linket er ugyldigt, udløbet eller allerede brugt. Bed om et nyt.',
  checkInboxTitle: 'Tjek din indbakke',
  sentTo: 'Vi har sendt et link til',
  signupSentHint: 'Åbn det på en hvilken som helst enhed for at gøre din virksomhed klar.',
  inviteSentHint: 'Åbn det på en hvilken som helst enhed for at blive en del af teamet.',
  resetSentTo: 'Hvis der findes en Poneva-konto for',
  resetSentHint: 'er et link til nulstilling på vej. Tjek også din spammappe.',
  resend: 'Send igen',
  resendIn: 'Send igen om {seconds} s',
  resent: 'Sendt igen. Tjek din indbakke.',
  resendFailed: 'Vi kunne ikke sende igen. Prøv om lidt.',
  signupExpired: 'Tilmeldingen er udløbet. Start forfra med din e-mail.',
  useAnotherEmail: 'Brug en anden e-mail',
  checkingInvite: 'Tjekker din invitation…',
  inviteUnavailableTitle: 'Vi kunne ikke tjekke invitationen',
  inviteUnavailableLead: 'Tjek din forbindelse, og prøv igen.',
  tryAgain: 'Prøv igen',
  inviteInvalidTitle: 'Invitationen kan ikke bruges',
  inviteInvalidLead:
    'Den er måske udløbet eller allerede brugt. Bed den person, der inviterede dig, om et nyt link.',
  createCompany: 'Opret en virksomhed',
  backToInvite: 'Tilbage til din invitation',
};

const nl: EntryCopy = {
  signupTitle: 'Richt je bedrijf in',
  signupLead:
    "Meld je aan met je zakelijke e-mail. Jij wordt eigenaar, en collega's komen erbij via een uitnodiging.",
  loginTitle: 'Inloggen bij Poneva',
  inviteTitle: 'Word lid van {company}',
  forgotTitle: 'Je wachtwoord opnieuw instellen',
  forgotLead: 'Vul je e-mailadres in, dan sturen we je een eenmalige link.',
  resetTitle: 'Kies een nieuw wachtwoord',
  resetLead: 'De link werkt één keer en verloopt 30 minuten nadat hij is verzonden.',
  resetDoneTitle: 'Wachtwoord gewijzigd',
  resetDoneLead: 'Je bent overal uitgelogd. Log in met je nieuwe wachtwoord.',
  companyLabel: 'Bedrijfsnaam',
  workEmailLabel: 'Zakelijke e-mail',
  emailLabel: 'E-mail',
  passwordLabel: 'Wachtwoord',
  newPasswordLabel: 'Nieuw wachtwoord',
  confirmPasswordLabel: 'Nieuw wachtwoord bevestigen',
  passwordHelp: 'Minimaal 15 tekens',
  signingUpAs: 'Aanmelden als {email}',
  forgotPassword: 'Wachtwoord vergeten?',
  sendResetLink: 'Herstellink versturen',
  saveNewPassword: 'Nieuw wachtwoord opslaan',
  continueToLogin: 'Door naar inloggen',
  creatingAccount: 'Account wordt aangemaakt…',
  loggingIn: 'Bezig met inloggen…',
  sending: 'Bezig met versturen…',
  saving: 'Bezig met opslaan…',
  confirming: 'Bezig met bevestigen…',
  companyRequired: 'Vul de naam van je bedrijf in.',
  companyTooLong: 'De bedrijfsnaam mag maximaal 120 tekens lang zijn.',
  emailRequired: 'Vul je e-mailadres in.',
  emailInvalid: 'Vul een geldig e-mailadres in, zoals naam@bedrijf.nl.',
  workEmailRequired: 'Gebruik je zakelijke e-mail. Uitgenodigd? Open dan je uitnodigingslink.',
  passwordRequired: 'Vul je wachtwoord in.',
  passwordTooShort: 'Gebruik minimaal 15 tekens.',
  passwordTooLong: 'Het wachtwoord is te lang.',
  passwordCompromised: 'Dit wachtwoord komt voor in bekende datalekken. Kies een ander wachtwoord.',
  passwordMismatch: 'De wachtwoorden komen niet overeen.',
  resetExpired: 'Deze link is ongeldig, verlopen of al gebruikt. Vraag een nieuwe aan.',
  checkInboxTitle: 'Kijk in je inbox',
  sentTo: 'We hebben een link gestuurd naar',
  signupSentHint: 'Open hem op elk gewenst apparaat om je bedrijf verder in te richten.',
  inviteSentHint: 'Open hem op elk gewenst apparaat om lid te worden.',
  resetSentTo: 'Als er een Poneva-account bestaat voor',
  resetSentHint: 'is er een resetlink onderweg. Kijk ook in je spammap.',
  resend: 'Opnieuw versturen',
  resendIn: 'Opnieuw versturen over {seconds} s',
  resent: 'Opnieuw verstuurd. Kijk in je inbox.',
  resendFailed: 'Opnieuw versturen is mislukt. Probeer het zo nog eens.',
  signupExpired: 'Deze aanmelding is verlopen. Begin opnieuw met je e-mailadres.',
  useAnotherEmail: 'Ander e-mailadres gebruiken',
  checkingInvite: 'Je uitnodiging wordt gecontroleerd…',
  inviteUnavailableTitle: 'We konden deze uitnodiging niet controleren',
  inviteUnavailableLead: 'Controleer je verbinding en probeer het opnieuw.',
  tryAgain: 'Opnieuw proberen',
  inviteInvalidTitle: 'Deze uitnodiging kan niet worden gebruikt',
  inviteInvalidLead:
    'Misschien is ze verlopen of al gebruikt. Vraag de persoon die je heeft uitgenodigd om een nieuwe link.',
  createCompany: 'Een bedrijf aanmaken',
  backToInvite: 'Terug naar je uitnodiging',
};

const et: EntryCopy = {
  signupTitle: 'Seadista oma ettevõte',
  signupLead: 'Registreeru oma tööe-postiga. Sinust saab omanik ja kolleegid liituvad kutse kaudu.',
  loginTitle: 'Logi Ponevasse sisse',
  inviteTitle: 'Liitu: {company}',
  forgotTitle: 'Lähtesta oma parool',
  forgotLead: 'Sisesta oma e-posti aadress ja me saadame sulle ühekordse lingi.',
  resetTitle: 'Vali uus parool',
  resetLead: 'Link töötab ühe korra ja aegub 30 minutit pärast saatmist.',
  resetDoneTitle: 'Parool on muudetud',
  resetDoneLead: 'Sind logiti kõikjal välja. Logi sisse uue parooliga.',
  companyLabel: 'Ettevõtte nimi',
  workEmailLabel: 'Tööe-post',
  emailLabel: 'E-post',
  passwordLabel: 'Parool',
  newPasswordLabel: 'Uus parool',
  confirmPasswordLabel: 'Kinnita uus parool',
  passwordHelp: 'Vähemalt 15 tähemärki',
  signingUpAs: 'Registreerud kui {email}',
  forgotPassword: 'Unustasid parooli?',
  sendResetLink: 'Saada lähtestamislink',
  saveNewPassword: 'Salvesta uus parool',
  continueToLogin: 'Jätka sisselogimisega',
  creatingAccount: 'Konto loomine…',
  loggingIn: 'Sisselogimine…',
  sending: 'Saatmine…',
  saving: 'Salvestamine…',
  confirming: 'Kinnitamine…',
  companyRequired: 'Sisesta oma ettevõtte nimi.',
  companyTooLong: 'Ettevõtte nimi võib olla kuni 120 tähemärki.',
  emailRequired: 'Sisesta oma e-posti aadress.',
  emailInvalid: 'Sisesta kehtiv e-posti aadress, näiteks nimi@ettevote.ee.',
  workEmailRequired: 'Kasuta oma tööe-posti. Said kutse? Ava selle asemel kutselink.',
  passwordRequired: 'Sisesta oma parool.',
  passwordTooShort: 'Kasuta vähemalt 15 tähemärki.',
  passwordTooLong: 'Parool on liiga pikk.',
  passwordCompromised: 'See parool esineb teadaolevates andmelekete loendites. Vali mõni muu.',
  passwordMismatch: 'Paroolid ei kattu.',
  resetExpired: 'See link on kehtetu, aegunud või juba kasutatud. Küsi uus.',
  checkInboxTitle: 'Vaata oma postkasti',
  sentTo: 'Saatsime lingi aadressile',
  signupSentHint: 'Ava see mis tahes seadmes, et oma ettevõtte seadistamine lõpetada.',
  inviteSentHint: 'Ava see mis tahes seadmes, et liitumine lõpetada.',
  resetSentTo: 'Kui aadressiga',
  resetSentHint: 'on seotud Poneva konto, on lähtestamislink teel. Vaata ka rämpspostikausta.',
  resend: 'Saada uuesti',
  resendIn: 'Saada uuesti {seconds} s pärast',
  resent: 'Saadetud uuesti. Vaata oma postkasti.',
  resendFailed: 'Uuesti saatmine ei õnnestunud. Proovi hetke pärast uuesti.',
  signupExpired: 'See registreerimine on aegunud. Alusta uuesti oma e-posti aadressiga.',
  useAnotherEmail: 'Kasuta teist e-posti aadressi',
  checkingInvite: 'Kontrollime sinu kutset…',
  inviteUnavailableTitle: 'Seda kutset ei õnnestunud kontrollida',
  inviteUnavailableLead: 'Kontrolli ühendust ja proovi uuesti.',
  tryAgain: 'Proovi uuesti',
  inviteInvalidTitle: 'Seda kutset ei saa kasutada',
  inviteInvalidLead:
    'See võib olla aegunud või juba kasutatud. Küsi sind kutsunud inimeselt uus link.',
  createCompany: 'Loo ettevõte',
  backToInvite: 'Tagasi kutse juurde',
};

const fi: EntryCopy = {
  signupTitle: 'Ota yrityksesi käyttöön',
  signupLead:
    'Rekisteröidy työsähköpostillasi. Sinusta tulee omistaja, ja kollegat liittyvät kutsulla.',
  loginTitle: 'Kirjaudu Ponevaan',
  inviteTitle: 'Liity: {company}',
  forgotTitle: 'Nollaa salasanasi',
  forgotLead: 'Anna sähköpostiosoitteesi, niin lähetämme kertakäyttöisen linkin.',
  resetTitle: 'Valitse uusi salasana',
  resetLead: 'Linkki toimii kerran ja vanhenee 30 minuuttia lähettämisen jälkeen.',
  resetDoneTitle: 'Salasana vaihdettu',
  resetDoneLead: 'Sinut on kirjattu ulos kaikkialta. Kirjaudu uudella salasanallasi.',
  companyLabel: 'Yrityksen nimi',
  workEmailLabel: 'Työsähköposti',
  emailLabel: 'Sähköposti',
  passwordLabel: 'Salasana',
  newPasswordLabel: 'Uusi salasana',
  confirmPasswordLabel: 'Vahvista uusi salasana',
  passwordHelp: 'Vähintään 15 merkkiä',
  signingUpAs: 'Rekisteröidyt osoitteella {email}',
  forgotPassword: 'Unohtuiko salasana?',
  sendResetLink: 'Lähetä nollauslinkki',
  saveNewPassword: 'Tallenna uusi salasana',
  continueToLogin: 'Jatka kirjautumiseen',
  creatingAccount: 'Luodaan tiliä…',
  loggingIn: 'Kirjaudutaan…',
  sending: 'Lähetetään…',
  saving: 'Tallennetaan…',
  confirming: 'Vahvistetaan…',
  companyRequired: 'Anna yrityksesi nimi.',
  companyTooLong: 'Yrityksen nimessä voi olla enintään 120 merkkiä.',
  emailRequired: 'Anna sähköpostiosoitteesi.',
  emailInvalid: 'Anna kelvollinen sähköpostiosoite, esimerkiksi nimi@yritys.fi.',
  workEmailRequired: 'Käytä työsähköpostiasi. Saitko kutsun? Avaa kutsulinkki sen sijaan.',
  passwordRequired: 'Anna salasanasi.',
  passwordTooShort: 'Käytä vähintään 15 merkkiä.',
  passwordTooLong: 'Salasana on liian pitkä.',
  passwordCompromised: 'Tämä salasana löytyy tunnetuista tietovuodoista. Valitse toinen.',
  passwordMismatch: 'Salasanat eivät täsmää.',
  resetExpired: 'Linkki on virheellinen, vanhentunut tai jo käytetty. Pyydä uusi.',
  checkInboxTitle: 'Tarkista sähköpostisi',
  sentTo: 'Lähetimme linkin osoitteeseen',
  signupSentHint: 'Avaa se millä tahansa laitteella viimeistelläksesi yrityksesi käyttöönoton.',
  inviteSentHint: 'Avaa se millä tahansa laitteella liittyäksesi.',
  resetSentTo: 'Jos osoitteella',
  resetSentHint: 'on Poneva-tili, palautuslinkki on matkalla. Tarkista myös roskapostikansio.',
  resend: 'Lähetä uudelleen',
  resendIn: 'Lähetä uudelleen {seconds} s kuluttua',
  resent: 'Lähetetty uudelleen. Tarkista sähköpostisi.',
  resendFailed: 'Uudelleenlähetys epäonnistui. Yritä hetken päästä uudelleen.',
  signupExpired: 'Rekisteröityminen on vanhentunut. Aloita alusta sähköpostiosoitteellasi.',
  useAnotherEmail: 'Käytä toista sähköpostiosoitetta',
  checkingInvite: 'Tarkistetaan kutsuasi…',
  inviteUnavailableTitle: 'Kutsua ei voitu tarkistaa',
  inviteUnavailableLead: 'Tarkista yhteys ja yritä uudelleen.',
  tryAgain: 'Yritä uudelleen',
  inviteInvalidTitle: 'Tätä kutsua ei voi käyttää',
  inviteInvalidLead:
    'Se on voinut vanhentua tai olla jo käytetty. Pyydä uusi linkki henkilöltä, joka kutsui sinut.',
  createCompany: 'Luo yritys',
  backToInvite: 'Takaisin kutsuun',
};

const fr: EntryCopy = {
  signupTitle: 'Configurez votre entreprise',
  signupLead:
    'Inscrivez-vous avec votre adresse professionnelle. Vous en devenez propriétaire, et votre équipe vous rejoint sur invitation.',
  loginTitle: 'Se connecter à Poneva',
  inviteTitle: 'Rejoindre {company}',
  forgotTitle: 'Réinitialiser votre mot de passe',
  forgotLead: 'Saisissez votre adresse e-mail et nous vous enverrons un lien à usage unique.',
  resetTitle: 'Choisissez un nouveau mot de passe',
  resetLead: 'Le lien fonctionne une seule fois et expire 30 minutes après son envoi.',
  resetDoneTitle: 'Mot de passe modifié',
  resetDoneLead:
    'Vous avez été déconnecté partout. Connectez-vous avec votre nouveau mot de passe.',
  companyLabel: 'Nom de l’entreprise',
  workEmailLabel: 'E-mail professionnel',
  emailLabel: 'E-mail',
  passwordLabel: 'Mot de passe',
  newPasswordLabel: 'Nouveau mot de passe',
  confirmPasswordLabel: 'Confirmer le nouveau mot de passe',
  passwordHelp: 'Au moins 15 caractères',
  signingUpAs: 'Inscription en tant que {email}',
  forgotPassword: 'Mot de passe oublié ?',
  sendResetLink: 'Envoyer le lien de réinitialisation',
  saveNewPassword: 'Enregistrer le nouveau mot de passe',
  continueToLogin: 'Continuer vers la connexion',
  creatingAccount: 'Création du compte…',
  loggingIn: 'Connexion…',
  sending: 'Envoi…',
  saving: 'Enregistrement…',
  confirming: 'Confirmation…',
  companyRequired: 'Saisissez le nom de votre entreprise.',
  companyTooLong: 'Le nom de l’entreprise ne doit pas dépasser 120 caractères.',
  emailRequired: 'Saisissez votre adresse e-mail.',
  emailInvalid: 'Saisissez une adresse e-mail valide, par exemple nom@entreprise.fr.',
  workEmailRequired:
    'Utilisez votre adresse professionnelle. Vous avez été invité ? Ouvrez plutôt votre lien d’invitation.',
  passwordRequired: 'Saisissez votre mot de passe.',
  passwordTooShort: 'Utilisez au moins 15 caractères.',
  passwordTooLong: 'Le mot de passe est trop long.',
  passwordCompromised:
    'Ce mot de passe figure dans des fuites de données connues. Choisissez-en un autre.',
  passwordMismatch: 'Les mots de passe ne correspondent pas.',
  resetExpired: 'Ce lien est invalide, expiré ou déjà utilisé. Demandez-en un nouveau.',
  checkInboxTitle: 'Consultez votre boîte de réception',
  sentTo: 'Nous avons envoyé un lien à',
  signupSentHint:
    'Ouvrez-le sur n’importe quel appareil pour finir de configurer votre entreprise.',
  inviteSentHint: 'Ouvrez-le sur n’importe quel appareil pour finaliser votre adhésion.',
  resetSentTo: 'Si un compte Poneva existe pour',
  resetSentHint:
    'un lien de réinitialisation est en route. Vérifiez aussi vos courriers indésirables.',
  resend: 'Renvoyer l’e-mail',
  resendIn: 'Renvoyer dans {seconds} s',
  resent: 'Renvoyé. Consultez votre boîte de réception.',
  resendFailed: 'Impossible de renvoyer l’e-mail. Réessayez dans un instant.',
  signupExpired: 'Cette inscription a expiré. Recommencez avec votre adresse e-mail.',
  useAnotherEmail: 'Utiliser une autre adresse',
  checkingInvite: 'Vérification de votre invitation…',
  inviteUnavailableTitle: 'Impossible de vérifier cette invitation',
  inviteUnavailableLead: 'Vérifiez votre connexion et réessayez.',
  tryAgain: 'Réessayer',
  inviteInvalidTitle: 'Cette invitation ne peut pas être utilisée',
  inviteInvalidLead:
    'Elle a peut-être expiré ou déjà été utilisée. Demandez un nouveau lien à la personne qui vous a invité.',
  createCompany: 'Créer une entreprise',
  backToInvite: 'Retour à votre invitation',
};

const de: EntryCopy = {
  signupTitle: 'Richten Sie Ihr Unternehmen ein',
  signupLead:
    'Registrieren Sie sich mit Ihrer geschäftlichen E-Mail. Sie werden Inhaber, Ihr Team kommt per Einladung dazu.',
  loginTitle: 'Bei Poneva anmelden',
  inviteTitle: '{company} beitreten',
  forgotTitle: 'Passwort zurücksetzen',
  forgotLead: 'Geben Sie Ihre E-Mail-Adresse ein, und wir senden Ihnen einen einmaligen Link.',
  resetTitle: 'Neues Passwort wählen',
  resetLead: 'Der Link funktioniert einmal und läuft 30 Minuten nach dem Versand ab.',
  resetDoneTitle: 'Passwort geändert',
  resetDoneLead: 'Sie wurden überall abgemeldet. Melden Sie sich mit Ihrem neuen Passwort an.',
  companyLabel: 'Name des Unternehmens',
  workEmailLabel: 'Geschäftliche E-Mail',
  emailLabel: 'E-Mail',
  passwordLabel: 'Passwort',
  newPasswordLabel: 'Neues Passwort',
  confirmPasswordLabel: 'Neues Passwort bestätigen',
  passwordHelp: 'Mindestens 15 Zeichen',
  signingUpAs: 'Registrierung als {email}',
  forgotPassword: 'Passwort vergessen?',
  sendResetLink: 'Link zum Zurücksetzen senden',
  saveNewPassword: 'Neues Passwort speichern',
  continueToLogin: 'Weiter zur Anmeldung',
  creatingAccount: 'Konto wird erstellt…',
  loggingIn: 'Anmeldung läuft…',
  sending: 'Wird gesendet…',
  saving: 'Wird gespeichert…',
  confirming: 'Wird bestätigt…',
  companyRequired: 'Geben Sie den Namen Ihres Unternehmens ein.',
  companyTooLong: 'Der Unternehmensname darf höchstens 120 Zeichen lang sein.',
  emailRequired: 'Geben Sie Ihre E-Mail-Adresse ein.',
  emailInvalid: 'Geben Sie eine gültige E-Mail-Adresse ein, z. B. name@firma.de.',
  workEmailRequired:
    'Verwenden Sie Ihre geschäftliche E-Mail. Eingeladen? Öffnen Sie stattdessen Ihren Einladungslink.',
  passwordRequired: 'Geben Sie Ihr Passwort ein.',
  passwordTooShort: 'Verwenden Sie mindestens 15 Zeichen.',
  passwordTooLong: 'Das Passwort ist zu lang.',
  passwordCompromised:
    'Dieses Passwort taucht in bekannten Datenlecks auf. Wählen Sie ein anderes.',
  passwordMismatch: 'Die Passwörter stimmen nicht überein.',
  resetExpired:
    'Der Link ist ungültig, abgelaufen oder wurde bereits verwendet. Fordern Sie einen neuen an.',
  checkInboxTitle: 'Prüfen Sie Ihren Posteingang',
  sentTo: 'Wir haben einen Link gesendet an',
  signupSentHint:
    'Öffnen Sie ihn auf einem beliebigen Gerät, um Ihr Unternehmen fertig einzurichten.',
  inviteSentHint: 'Öffnen Sie ihn auf einem beliebigen Gerät, um beizutreten.',
  resetSentTo: 'Falls ein Poneva-Konto für',
  resetSentHint:
    'existiert, ist ein Link zum Zurücksetzen unterwegs. Prüfen Sie auch Ihren Spam-Ordner.',
  resend: 'Erneut senden',
  resendIn: 'Erneut senden in {seconds} s',
  resent: 'Erneut gesendet. Prüfen Sie Ihren Posteingang.',
  resendFailed: 'Erneutes Senden fehlgeschlagen. Versuchen Sie es gleich noch einmal.',
  signupExpired:
    'Diese Registrierung ist abgelaufen. Beginnen Sie erneut mit Ihrer E-Mail-Adresse.',
  useAnotherEmail: 'Andere E-Mail verwenden',
  checkingInvite: 'Ihre Einladung wird geprüft…',
  inviteUnavailableTitle: 'Die Einladung konnte nicht geprüft werden',
  inviteUnavailableLead: 'Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
  tryAgain: 'Erneut versuchen',
  inviteInvalidTitle: 'Diese Einladung kann nicht verwendet werden',
  inviteInvalidLead:
    'Sie ist möglicherweise abgelaufen oder wurde bereits verwendet. Bitten Sie die Person, die Sie eingeladen hat, um einen neuen Link.',
  createCompany: 'Unternehmen erstellen',
  backToInvite: 'Zurück zu Ihrer Einladung',
};

const el: EntryCopy = {
  signupTitle: 'Ρυθμίστε την εταιρεία σας',
  signupLead:
    'Εγγραφείτε με το εταιρικό σας email. Γίνεστε ο κάτοχος και οι συνάδελφοι συμμετέχουν με πρόσκληση.',
  loginTitle: 'Σύνδεση στο Poneva',
  inviteTitle: 'Γίνετε μέλος της {company}',
  forgotTitle: 'Επαναφορά κωδικού πρόσβασης',
  forgotLead: 'Εισαγάγετε το email σας και θα σας στείλουμε έναν σύνδεσμο μίας χρήσης.',
  resetTitle: 'Επιλέξτε νέο κωδικό πρόσβασης',
  resetLead: 'Ο σύνδεσμος λειτουργεί μία φορά και λήγει 30 λεπτά μετά την αποστολή του.',
  resetDoneTitle: 'Ο κωδικός πρόσβασης άλλαξε',
  resetDoneLead: 'Αποσυνδεθήκατε παντού. Συνδεθείτε με τον νέο σας κωδικό πρόσβασης.',
  companyLabel: 'Όνομα εταιρείας',
  workEmailLabel: 'Εταιρικό email',
  emailLabel: 'Email',
  passwordLabel: 'Κωδικός πρόσβασης',
  newPasswordLabel: 'Νέος κωδικός πρόσβασης',
  confirmPasswordLabel: 'Επιβεβαίωση νέου κωδικού πρόσβασης',
  passwordHelp: 'Τουλάχιστον 15 χαρακτήρες',
  signingUpAs: 'Εγγραφή ως {email}',
  forgotPassword: 'Ξεχάσατε τον κωδικό;',
  sendResetLink: 'Αποστολή συνδέσμου επαναφοράς',
  saveNewPassword: 'Αποθήκευση νέου κωδικού',
  continueToLogin: 'Συνέχεια στη σύνδεση',
  creatingAccount: 'Δημιουργία λογαριασμού…',
  loggingIn: 'Σύνδεση…',
  sending: 'Αποστολή…',
  saving: 'Αποθήκευση…',
  confirming: 'Επιβεβαίωση…',
  companyRequired: 'Εισαγάγετε το όνομα της εταιρείας σας.',
  companyTooLong: 'Το όνομα της εταιρείας μπορεί να έχει έως 120 χαρακτήρες.',
  emailRequired: 'Εισαγάγετε τη διεύθυνση email σας.',
  emailInvalid: 'Εισαγάγετε έγκυρη διεύθυνση email, π.χ. name@company.com.',
  workEmailRequired:
    'Χρησιμοποιήστε το εταιρικό σας email. Έχετε πρόσκληση; Ανοίξτε τον σύνδεσμο της πρόσκλησης.',
  passwordRequired: 'Εισαγάγετε τον κωδικό πρόσβασής σας.',
  passwordTooShort: 'Χρησιμοποιήστε τουλάχιστον 15 χαρακτήρες.',
  passwordTooLong: 'Ο κωδικός πρόσβασης είναι πολύ μεγάλος.',
  passwordCompromised: 'Αυτός ο κωδικός εμφανίζεται σε γνωστές διαρροές δεδομένων. Επιλέξτε άλλον.',
  passwordMismatch: 'Οι κωδικοί πρόσβασης δεν ταιριάζουν.',
  resetExpired: 'Ο σύνδεσμος δεν είναι έγκυρος, έχει λήξει ή έχει ήδη χρησιμοποιηθεί. Ζητήστε νέο.',
  checkInboxTitle: 'Ελέγξτε τα εισερχόμενά σας',
  sentTo: 'Στείλαμε έναν σύνδεσμο στο',
  signupSentHint:
    'Ανοίξτε τον σε οποιαδήποτε συσκευή για να ολοκληρώσετε τη ρύθμιση της εταιρείας σας.',
  inviteSentHint: 'Ανοίξτε τον σε οποιαδήποτε συσκευή για να ολοκληρώσετε την εγγραφή σας.',
  resetSentTo: 'Αν υπάρχει λογαριασμός Poneva για το',
  resetSentHint: 'ένας σύνδεσμος επαναφοράς είναι καθ’ οδόν. Ελέγξτε και τα ανεπιθύμητα.',
  resend: 'Νέα αποστολή email',
  resendIn: 'Νέα αποστολή σε {seconds} δευτ.',
  resent: 'Στάλθηκε ξανά. Ελέγξτε τα εισερχόμενά σας.',
  resendFailed: 'Δεν μπορέσαμε να το στείλουμε ξανά. Δοκιμάστε σε λίγο.',
  signupExpired: 'Η εγγραφή έληξε. Ξεκινήστε ξανά με το email σας.',
  useAnotherEmail: 'Χρήση άλλου email',
  checkingInvite: 'Ελέγχουμε την πρόσκλησή σας…',
  inviteUnavailableTitle: 'Δεν μπορέσαμε να ελέγξουμε αυτή την πρόσκληση',
  inviteUnavailableLead: 'Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',
  tryAgain: 'Δοκιμάστε ξανά',
  inviteInvalidTitle: 'Αυτή η πρόσκληση δεν μπορεί να χρησιμοποιηθεί',
  inviteInvalidLead:
    'Μπορεί να έχει λήξει ή να έχει ήδη χρησιμοποιηθεί. Ζητήστε νέο σύνδεσμο από το άτομο που σας προσκάλεσε.',
  createCompany: 'Δημιουργία εταιρείας',
  backToInvite: 'Πίσω στην πρόσκλησή σας',
};

const hu: EntryCopy = {
  signupTitle: 'Állítsa be a cégét',
  signupLead:
    'Regisztráljon munkahelyi e-mail-címével. Ön lesz a tulajdonos, a kollégák meghívással csatlakoznak.',
  loginTitle: 'Bejelentkezés a Ponevába',
  inviteTitle: 'Csatlakozás: {company}',
  forgotTitle: 'Jelszó visszaállítása',
  forgotLead: 'Adja meg e-mail-címét, és küldünk egy egyszer használatos linket.',
  resetTitle: 'Válasszon új jelszót',
  resetLead: 'A link egyszer használható, és a küldés után 30 perccel lejár.',
  resetDoneTitle: 'Jelszó módosítva',
  resetDoneLead: 'Mindenhol kijelentkeztettük. Jelentkezzen be az új jelszavával.',
  companyLabel: 'Cégnév',
  workEmailLabel: 'Munkahelyi e-mail-cím',
  emailLabel: 'E-mail-cím',
  passwordLabel: 'Jelszó',
  newPasswordLabel: 'Új jelszó',
  confirmPasswordLabel: 'Új jelszó megerősítése',
  passwordHelp: 'Legalább 15 karakter',
  signingUpAs: 'Regisztráció mint {email}',
  forgotPassword: 'Elfelejtette a jelszavát?',
  sendResetLink: 'Visszaállító link küldése',
  saveNewPassword: 'Új jelszó mentése',
  continueToLogin: 'Tovább a bejelentkezéshez',
  creatingAccount: 'Fiók létrehozása…',
  loggingIn: 'Bejelentkezés…',
  sending: 'Küldés…',
  saving: 'Mentés…',
  confirming: 'Megerősítés…',
  companyRequired: 'Adja meg a cég nevét.',
  companyTooLong: 'A cégnév legfeljebb 120 karakter lehet.',
  emailRequired: 'Adja meg e-mail-címét.',
  emailInvalid: 'Adjon meg érvényes e-mail-címet, például nev@ceg.hu.',
  workEmailRequired:
    'Használja munkahelyi e-mail-címét. Meghívást kapott? Nyissa meg inkább a meghívó linkjét.',
  passwordRequired: 'Adja meg jelszavát.',
  passwordTooShort: 'Legalább 15 karaktert használjon.',
  passwordTooLong: 'A jelszó túl hosszú.',
  passwordCompromised: 'Ez a jelszó ismert adatszivárgásokban szerepel. Válasszon másikat.',
  passwordMismatch: 'A jelszavak nem egyeznek.',
  resetExpired: 'A link érvénytelen, lejárt vagy már felhasználták. Kérjen újat.',
  checkInboxTitle: 'Nézze meg a beérkező leveleit',
  sentTo: 'Linket küldtünk ide:',
  signupSentHint: 'Nyissa meg bármely eszközön a cég beállításának befejezéséhez.',
  inviteSentHint: 'Nyissa meg bármely eszközön a csatlakozás befejezéséhez.',
  resetSentTo: 'Ha tartozik Poneva-fiók ehhez a címhez:',
  resetSentHint: 'a visszaállító link már úton van. Nézze meg a levélszemét mappát is.',
  resend: 'E-mail újraküldése',
  resendIn: 'Újraküldés {seconds} mp múlva',
  resent: 'Újra elküldtük. Nézze meg a beérkező leveleit.',
  resendFailed: 'Nem sikerült újra elküldeni. Próbálja újra egy kicsit később.',
  signupExpired: 'A regisztráció lejárt. Kezdje újra az e-mail-címével.',
  useAnotherEmail: 'Másik e-mail-cím használata',
  checkingInvite: 'Meghívó ellenőrzése…',
  inviteUnavailableTitle: 'Nem sikerült ellenőrizni ezt a meghívót',
  inviteUnavailableLead: 'Ellenőrizze a kapcsolatot, és próbálja újra.',
  tryAgain: 'Próbálja újra',
  inviteInvalidTitle: 'Ez a meghívó nem használható',
  inviteInvalidLead:
    'Lehet, hogy lejárt vagy már felhasználták. Kérjen új linket attól, aki meghívta.',
  createCompany: 'Cég létrehozása',
  backToInvite: 'Vissza a meghívóhoz',
};

const ga: EntryCopy = {
  signupTitle: 'Socraigh do chomhlacht',
  signupLead:
    'Cláraigh le do ríomhphost oibre. Is tusa an t-úinéir, agus tagann comhghleacaithe isteach trí chuireadh.',
  loginTitle: 'Logáil isteach in Poneva',
  inviteTitle: 'Bí páirteach in {company}',
  forgotTitle: 'Athshocraigh do phasfhocal',
  forgotLead: 'Cuir isteach do ríomhphost agus seolfaimid nasc aonúsáide chugat.',
  resetTitle: 'Roghnaigh pasfhocal nua',
  resetLead: 'Oibríonn an nasc uair amháin agus rachaidh sé in éag 30 nóiméad tar éis a sheolta.',
  resetDoneTitle: 'Athraíodh an pasfhocal',
  resetDoneLead: 'Logáladh amach thú i ngach áit. Logáil isteach le do phasfhocal nua.',
  companyLabel: 'Ainm an chomhlachta',
  workEmailLabel: 'Ríomhphost oibre',
  emailLabel: 'Ríomhphost',
  passwordLabel: 'Pasfhocal',
  newPasswordLabel: 'Pasfhocal nua',
  confirmPasswordLabel: 'Deimhnigh an pasfhocal nua',
  passwordHelp: '15 charachtar ar a laghad',
  signingUpAs: 'Ag clárú mar {email}',
  forgotPassword: 'Pasfhocal dearmadta?',
  sendResetLink: 'Seol nasc athshocraithe',
  saveNewPassword: 'Sábháil an pasfhocal nua',
  continueToLogin: 'Lean ar aghaidh chun logáil isteach',
  creatingAccount: 'Cuntas á chruthú…',
  loggingIn: 'Ag logáil isteach…',
  sending: 'Á sheoladh…',
  saving: 'Á shábháil…',
  confirming: 'Á dheimhniú…',
  companyRequired: 'Cuir isteach ainm do chomhlachta.',
  companyTooLong: 'Ní féidir níos mó ná 120 carachtar a bheith in ainm an chomhlachta.',
  emailRequired: 'Cuir isteach do sheoladh ríomhphoist.',
  emailInvalid: 'Cuir isteach seoladh ríomhphoist bailí, mar shampla ainm@comhlacht.ie.',
  workEmailRequired:
    'Úsáid do ríomhphost oibre. An bhfuair tú cuireadh? Oscail nasc an chuiridh ina ionad.',
  passwordRequired: 'Cuir isteach do phasfhocal.',
  passwordTooShort: 'Úsáid 15 charachtar ar a laghad.',
  passwordTooLong: 'Tá an pasfhocal rófhada.',
  passwordCompromised:
    'Tá an pasfhocal seo le feiceáil i sceitheanna sonraí aitheanta. Roghnaigh ceann eile.',
  passwordMismatch: 'Ní hionann na pasfhocail.',
  resetExpired: 'Tá an nasc seo neamhbhailí, imithe in éag nó úsáidte cheana. Iarr ceann nua.',
  checkInboxTitle: 'Seiceáil do bhosca isteach',
  sentTo: 'Sheolamar nasc chuig',
  signupSentHint: 'Oscail é ar aon ghléas chun socrú do chomhlachta a chríochnú.',
  inviteSentHint: 'Oscail é ar aon ghléas chun an clárú a chríochnú.',
  resetSentTo: 'Má tá cuntas Poneva ag',
  resetSentHint: 'tá nasc athshocraithe ar an mbealach. Seiceáil d’fhillteán turscair freisin.',
  resend: 'Seol an ríomhphost arís',
  resendIn: 'Seol arís i gceann {seconds} s',
  resent: 'Seoladh arís é. Seiceáil do bhosca isteach.',
  resendFailed: 'Níorbh fhéidir linn é a sheoladh arís. Bain triail eile as i gceann tamaillín.',
  signupExpired: 'Tá an clárú seo imithe in éag. Tosaigh arís le do ríomhphost.',
  useAnotherEmail: 'Úsáid ríomhphost eile',
  checkingInvite: 'Do chuireadh á sheiceáil…',
  inviteUnavailableTitle: 'Níorbh fhéidir linn an cuireadh seo a sheiceáil',
  inviteUnavailableLead: 'Seiceáil do nasc agus bain triail eile as.',
  tryAgain: 'Bain triail eile as',
  inviteInvalidTitle: 'Ní féidir an cuireadh seo a úsáid',
  inviteInvalidLead:
    'B’fhéidir go bhfuil sé imithe in éag nó úsáidte cheana. Iarr nasc nua ar an duine a thug cuireadh duit.',
  createCompany: 'Cruthaigh comhlacht',
  backToInvite: 'Ar ais chuig do chuireadh',
};

const it: EntryCopy = {
  signupTitle: 'Configura la tua azienda',
  signupLead:
    'Registrati con la tua email di lavoro. Diventi il proprietario e i colleghi si uniscono su invito.',
  loginTitle: 'Accedi a Poneva',
  inviteTitle: 'Unisciti a {company}',
  forgotTitle: 'Reimposta la password',
  forgotLead: 'Inserisci la tua email e ti invieremo un link monouso.',
  resetTitle: 'Scegli una nuova password',
  resetLead: 'Il link funziona una sola volta e scade 30 minuti dopo l’invio.',
  resetDoneTitle: 'Password modificata',
  resetDoneLead: 'Sei stato disconnesso ovunque. Accedi con la nuova password.',
  companyLabel: 'Nome dell’azienda',
  workEmailLabel: 'Email di lavoro',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  newPasswordLabel: 'Nuova password',
  confirmPasswordLabel: 'Conferma la nuova password',
  passwordHelp: 'Almeno 15 caratteri',
  signingUpAs: 'Registrazione come {email}',
  forgotPassword: 'Password dimenticata?',
  sendResetLink: 'Invia link di reimpostazione',
  saveNewPassword: 'Salva la nuova password',
  continueToLogin: 'Continua all’accesso',
  creatingAccount: 'Creazione dell’account…',
  loggingIn: 'Accesso in corso…',
  sending: 'Invio in corso…',
  saving: 'Salvataggio…',
  confirming: 'Conferma in corso…',
  companyRequired: 'Inserisci il nome della tua azienda.',
  companyTooLong: 'Il nome dell’azienda può contenere al massimo 120 caratteri.',
  emailRequired: 'Inserisci il tuo indirizzo email.',
  emailInvalid: 'Inserisci un indirizzo email valido, ad esempio nome@azienda.it.',
  workEmailRequired:
    'Usa la tua email di lavoro. Hai ricevuto un invito? Apri invece il link d’invito.',
  passwordRequired: 'Inserisci la password.',
  passwordTooShort: 'Usa almeno 15 caratteri.',
  passwordTooLong: 'La password è troppo lunga.',
  passwordCompromised: 'Questa password compare in violazioni di dati note. Scegline un’altra.',
  passwordMismatch: 'Le password non coincidono.',
  resetExpired: 'Questo link non è valido, è scaduto o è già stato usato. Richiedine uno nuovo.',
  checkInboxTitle: 'Controlla la posta in arrivo',
  sentTo: 'Abbiamo inviato un link a',
  signupSentHint:
    'Aprilo su qualsiasi dispositivo per completare la configurazione della tua azienda.',
  inviteSentHint: 'Aprilo su qualsiasi dispositivo per completare l’adesione.',
  resetSentTo: 'Se esiste un account Poneva per',
  resetSentHint:
    'è in arrivo un link per reimpostare la password. Controlla anche la cartella spam.',
  resend: 'Invia di nuovo',
  resendIn: 'Invia di nuovo tra {seconds} s',
  resent: 'Inviata di nuovo. Controlla la posta in arrivo.',
  resendFailed: 'Non è stato possibile inviarla di nuovo. Riprova tra poco.',
  signupExpired: 'Questa registrazione è scaduta. Ricomincia con la tua email.',
  useAnotherEmail: 'Usa un’altra email',
  checkingInvite: 'Verifica dell’invito in corso…',
  inviteUnavailableTitle: 'Non è stato possibile verificare questo invito',
  inviteUnavailableLead: 'Controlla la connessione e riprova.',
  tryAgain: 'Riprova',
  inviteInvalidTitle: 'Questo invito non può essere usato',
  inviteInvalidLead:
    'Potrebbe essere scaduto o già usato. Chiedi un nuovo link alla persona che ti ha invitato.',
  createCompany: 'Crea un’azienda',
  backToInvite: 'Torna al tuo invito',
};

const lv: EntryCopy = {
  signupTitle: 'Iestatiet savu uzņēmumu',
  signupLead:
    'Reģistrējieties ar darba e-pastu. Jūs kļūsiet par īpašnieku, un kolēģi pievienosies ar uzaicinājumu.',
  loginTitle: 'Pieteikties Poneva',
  inviteTitle: 'Pievienoties: {company}',
  forgotTitle: 'Atiestatīt paroli',
  forgotLead: 'Ievadiet savu e-pasta adresi, un mēs nosūtīsim vienreizēju saiti.',
  resetTitle: 'Izvēlieties jaunu paroli',
  resetLead: 'Saite darbojas vienu reizi un zaudē derīgumu 30 minūtes pēc nosūtīšanas.',
  resetDoneTitle: 'Parole nomainīta',
  resetDoneLead: 'Jūs tikāt izrakstīts visur. Piesakieties ar jauno paroli.',
  companyLabel: 'Uzņēmuma nosaukums',
  workEmailLabel: 'Darba e-pasts',
  emailLabel: 'E-pasts',
  passwordLabel: 'Parole',
  newPasswordLabel: 'Jaunā parole',
  confirmPasswordLabel: 'Apstipriniet jauno paroli',
  passwordHelp: 'Vismaz 15 rakstzīmes',
  signingUpAs: 'Reģistrējaties kā {email}',
  forgotPassword: 'Aizmirsāt paroli?',
  sendResetLink: 'Nosūtīt atiestatīšanas saiti',
  saveNewPassword: 'Saglabāt jauno paroli',
  continueToLogin: 'Turpināt uz pieteikšanos',
  creatingAccount: 'Veido kontu…',
  loggingIn: 'Piesakās…',
  sending: 'Sūta…',
  saving: 'Saglabā…',
  confirming: 'Apstiprina…',
  companyRequired: 'Ievadiet uzņēmuma nosaukumu.',
  companyTooLong: 'Uzņēmuma nosaukumā var būt ne vairāk kā 120 rakstzīmes.',
  emailRequired: 'Ievadiet savu e-pasta adresi.',
  emailInvalid: 'Ievadiet derīgu e-pasta adresi, piemēram, vards@uznemums.lv.',
  workEmailRequired:
    'Izmantojiet darba e-pastu. Saņēmāt uzaicinājumu? Tā vietā atveriet uzaicinājuma saiti.',
  passwordRequired: 'Ievadiet paroli.',
  passwordTooShort: 'Izmantojiet vismaz 15 rakstzīmes.',
  passwordTooLong: 'Parole ir pārāk gara.',
  passwordCompromised: 'Šī parole ir atrodama zināmās datu noplūdēs. Izvēlieties citu.',
  passwordMismatch: 'Paroles nesakrīt.',
  resetExpired: 'Saite nav derīga, ir novecojusi vai jau izmantota. Pieprasiet jaunu.',
  checkInboxTitle: 'Pārbaudiet iesūtni',
  sentTo: 'Mēs nosūtījām saiti uz',
  signupSentHint: 'Atveriet to jebkurā ierīcē, lai pabeigtu uzņēmuma iestatīšanu.',
  inviteSentHint: 'Atveriet to jebkurā ierīcē, lai pabeigtu pievienošanos.',
  resetSentTo: 'Ja adresei',
  resetSentHint:
    'ir Poneva konts, atiestatīšanas saite jau ir ceļā. Pārbaudiet arī surogātpasta mapi.',
  resend: 'Sūtīt vēlreiz',
  resendIn: 'Sūtīt vēlreiz pēc {seconds} s',
  resent: 'Nosūtīts vēlreiz. Pārbaudiet iesūtni.',
  resendFailed: 'Neizdevās nosūtīt vēlreiz. Mēģiniet pēc brīža.',
  signupExpired: 'Šī reģistrācija ir novecojusi. Sāciet no jauna ar savu e-pastu.',
  useAnotherEmail: 'Izmantot citu e-pastu',
  checkingInvite: 'Pārbauda jūsu uzaicinājumu…',
  inviteUnavailableTitle: 'Neizdevās pārbaudīt šo uzaicinājumu',
  inviteUnavailableLead: 'Pārbaudiet savienojumu un mēģiniet vēlreiz.',
  tryAgain: 'Mēģināt vēlreiz',
  inviteInvalidTitle: 'Šo uzaicinājumu nevar izmantot',
  inviteInvalidLead:
    'Tas, iespējams, ir novecojis vai jau izmantots. Palūdziet jaunu saiti personai, kas jūs uzaicināja.',
  createCompany: 'Izveidot uzņēmumu',
  backToInvite: 'Atpakaļ pie uzaicinājuma',
};

const lt: EntryCopy = {
  signupTitle: 'Sukurkite savo įmonės erdvę',
  signupLead:
    'Registruokitės darbo el. paštu. Tapsite savininku, o kolegos prisijungs gavę kvietimą.',
  loginTitle: 'Prisijungti prie Poneva',
  inviteTitle: 'Prisijunkite prie {company}',
  forgotTitle: 'Atkurkite slaptažodį',
  forgotLead: 'Įveskite el. pašto adresą ir atsiųsime vienkartinę nuorodą.',
  resetTitle: 'Pasirinkite naują slaptažodį',
  resetLead: 'Nuoroda veikia vieną kartą ir nustoja galioti praėjus 30 minučių nuo išsiuntimo.',
  resetDoneTitle: 'Slaptažodis pakeistas',
  resetDoneLead: 'Buvote atjungti visur. Prisijunkite nauju slaptažodžiu.',
  companyLabel: 'Įmonės pavadinimas',
  workEmailLabel: 'Darbo el. paštas',
  emailLabel: 'El. paštas',
  passwordLabel: 'Slaptažodis',
  newPasswordLabel: 'Naujas slaptažodis',
  confirmPasswordLabel: 'Patvirtinkite naują slaptažodį',
  passwordHelp: 'Bent 15 simbolių',
  signingUpAs: 'Registruojatės kaip {email}',
  forgotPassword: 'Pamiršote slaptažodį?',
  sendResetLink: 'Siųsti atkūrimo nuorodą',
  saveNewPassword: 'Išsaugoti naują slaptažodį',
  continueToLogin: 'Tęsti prisijungimą',
  creatingAccount: 'Kuriama paskyra…',
  loggingIn: 'Jungiamasi…',
  sending: 'Siunčiama…',
  saving: 'Išsaugoma…',
  confirming: 'Tvirtinama…',
  companyRequired: 'Įveskite įmonės pavadinimą.',
  companyTooLong: 'Įmonės pavadinimą gali sudaryti ne daugiau kaip 120 simbolių.',
  emailRequired: 'Įveskite el. pašto adresą.',
  emailInvalid: 'Įveskite galiojantį el. pašto adresą, pvz., vardas@imone.lt.',
  workEmailRequired:
    'Naudokite darbo el. paštą. Gavote kvietimą? Verčiau atidarykite kvietimo nuorodą.',
  passwordRequired: 'Įveskite slaptažodį.',
  passwordTooShort: 'Naudokite bent 15 simbolių.',
  passwordTooLong: 'Slaptažodis per ilgas.',
  passwordCompromised:
    'Šis slaptažodis aptiktas žinomuose duomenų nutekėjimuose. Pasirinkite kitą.',
  passwordMismatch: 'Slaptažodžiai nesutampa.',
  resetExpired: 'Ši nuoroda negalioja, nebegalioja arba jau panaudota. Paprašykite naujos.',
  checkInboxTitle: 'Patikrinkite pašto dėžutę',
  sentTo: 'Išsiuntėme nuorodą adresu',
  signupSentHint: 'Atidarykite ją bet kuriame įrenginyje, kad užbaigtumėte įmonės nustatymą.',
  inviteSentHint: 'Atidarykite ją bet kuriame įrenginyje, kad užbaigtumėte prisijungimą.',
  resetSentTo: 'Jei adresu',
  resetSentHint:
    'yra Poneva paskyra, slaptažodžio atkūrimo nuoroda jau keliauja. Patikrinkite ir šlamšto aplanką.',
  resend: 'Siųsti dar kartą',
  resendIn: 'Siųsti dar kartą po {seconds} s',
  resent: 'Išsiųsta dar kartą. Patikrinkite pašto dėžutę.',
  resendFailed: 'Nepavyko išsiųsti dar kartą. Pabandykite po akimirkos.',
  signupExpired: 'Ši registracija nebegalioja. Pradėkite iš naujo nuo el. pašto adreso.',
  useAnotherEmail: 'Naudoti kitą el. paštą',
  checkingInvite: 'Tikrinamas jūsų kvietimas…',
  inviteUnavailableTitle: 'Nepavyko patikrinti šio kvietimo',
  inviteUnavailableLead: 'Patikrinkite ryšį ir bandykite dar kartą.',
  tryAgain: 'Bandyti dar kartą',
  inviteInvalidTitle: 'Šio kvietimo negalima naudoti',
  inviteInvalidLead:
    'Galbūt jis nebegalioja arba jau buvo panaudotas. Paprašykite jus pakvietusio asmens naujos nuorodos.',
  createCompany: 'Sukurti įmonę',
  backToInvite: 'Grįžti prie kvietimo',
};

const mt: EntryCopy = {
  signupTitle: 'Waqqaf il-kumpanija tiegħek',
  signupLead:
    'Irreġistra bl-email tax-xogħol tiegħek. Int issir is-sid, u l-kollegi jingħaqdu b’invit.',
  loginTitle: 'Idħol f’Poneva',
  inviteTitle: 'Ingħaqad ma’ {company}',
  forgotTitle: 'Irrisettja l-password tiegħek',
  forgotLead: 'Daħħal l-email tiegħek u nibagħtulek link għal darba waħda.',
  resetTitle: 'Agħżel password ġdida',
  resetLead: 'Il-link jaħdem darba biss u jiskadi 30 minuta wara li jintbagħat.',
  resetDoneTitle: 'Il-password inbidlet',
  resetDoneLead: 'Ħriġt minn kullimkien. Idħol bil-password il-ġdida tiegħek.',
  companyLabel: 'Isem il-kumpanija',
  workEmailLabel: 'Email tax-xogħol',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  newPasswordLabel: 'Password ġdida',
  confirmPasswordLabel: 'Ikkonferma l-password il-ġdida',
  passwordHelp: 'Mill-inqas 15-il karattru',
  signingUpAs: 'Qed tirreġistra bħala {email}',
  forgotPassword: 'Insejt il-password?',
  sendResetLink: 'Ibgħat link biex tirrisettja',
  saveNewPassword: 'Issejvja l-password il-ġdida',
  continueToLogin: 'Kompli biex tidħol',
  creatingAccount: 'Qed jinħoloq il-kont…',
  loggingIn: 'Qed tidħol…',
  sending: 'Qed jintbagħat…',
  saving: 'Qed jiġi ssejvjat…',
  confirming: 'Qed jiġi kkonfermat…',
  companyRequired: 'Daħħal l-isem tal-kumpanija tiegħek.',
  companyTooLong: 'L-isem tal-kumpanija jista’ jkollu sa 120 karattru.',
  emailRequired: 'Daħħal l-indirizz tal-email tiegħek.',
  emailInvalid: 'Daħħal indirizz tal-email validu, bħal isem@kumpanija.com.',
  workEmailRequired:
    'Uża l-email tax-xogħol tiegħek. Irċevejt invit? Iftaħ il-link tal-invit minflok.',
  passwordRequired: 'Daħħal il-password tiegħek.',
  passwordTooShort: 'Uża mill-inqas 15-il karattru.',
  passwordTooLong: 'Il-password hija twila wisq.',
  passwordCompromised: 'Din il-password tidher fi ksur magħruf tad-data. Agħżel oħra.',
  passwordMismatch: 'Il-passwords ma jaqblux.',
  resetExpired: 'Dan il-link mhuwiex validu, skada jew diġà ntuża. Itlob wieħed ġdid.',
  checkInboxTitle: 'Iċċekkja l-inbox tiegħek',
  sentTo: 'Bagħtna link lil',
  signupSentHint: 'Iftħu fuq kwalunkwe apparat biex tlesti t-twaqqif tal-kumpanija tiegħek.',
  inviteSentHint: 'Iftħu fuq kwalunkwe apparat biex tispiċċa tingħaqad.',
  resetSentTo: 'Jekk hemm kont ta’ Poneva għal',
  resetSentHint:
    'link biex terġa’ tissettja l-password qiegħed fi triqtu. Iċċekkja wkoll il-folder tal-ispam.',
  resend: 'Erġa’ ibgħat l-email',
  resendIn: 'Erġa’ ibgħat f’{seconds} s',
  resent: 'Intbagħat mill-ġdid. Iċċekkja l-inbox tiegħek.',
  resendFailed: 'Ma stajniex nerġgħu nibagħtuh. Erġa’ pprova ftit wara.',
  signupExpired: 'Din ir-reġistrazzjoni skadiet. Ibda mill-ġdid bl-email tiegħek.',
  useAnotherEmail: 'Uża email oħra',
  checkingInvite: 'Qed niċċekkjaw l-invit tiegħek…',
  inviteUnavailableTitle: 'Ma stajniex niċċekkjaw dan l-invit',
  inviteUnavailableLead: 'Iċċekkja l-konnessjoni tiegħek u erġa’ pprova.',
  tryAgain: 'Erġa’ pprova',
  inviteInvalidTitle: 'Dan l-invit ma jistax jintuża',
  inviteInvalidLead:
    'Jista’ jkun li skada jew diġà ntuża. Itlob link ġdid lill-persuna li stiednitek.',
  createCompany: 'Oħloq kumpanija',
  backToInvite: 'Lura għall-invit tiegħek',
};

const pl: EntryCopy = {
  signupTitle: 'Skonfiguruj swoją firmę',
  signupLead:
    'Zarejestruj się służbowym adresem e-mail. Zostaniesz właścicielem, a współpracownicy dołączą przez zaproszenie.',
  loginTitle: 'Zaloguj się do Poneva',
  inviteTitle: 'Dołącz do {company}',
  forgotTitle: 'Zresetuj hasło',
  forgotLead: 'Podaj swój adres e-mail, a wyślemy Ci jednorazowy link.',
  resetTitle: 'Wybierz nowe hasło',
  resetLead: 'Link działa jeden raz i wygasa 30 minut po wysłaniu.',
  resetDoneTitle: 'Hasło zostało zmienione',
  resetDoneLead: 'Wylogowano Cię wszędzie. Zaloguj się nowym hasłem.',
  companyLabel: 'Nazwa firmy',
  workEmailLabel: 'Służbowy e-mail',
  emailLabel: 'E-mail',
  passwordLabel: 'Hasło',
  newPasswordLabel: 'Nowe hasło',
  confirmPasswordLabel: 'Potwierdź nowe hasło',
  passwordHelp: 'Co najmniej 15 znaków',
  signingUpAs: 'Rejestrujesz się jako {email}',
  forgotPassword: 'Nie pamiętasz hasła?',
  sendResetLink: 'Wyślij link do resetowania',
  saveNewPassword: 'Zapisz nowe hasło',
  continueToLogin: 'Przejdź do logowania',
  creatingAccount: 'Tworzenie konta…',
  loggingIn: 'Logowanie…',
  sending: 'Wysyłanie…',
  saving: 'Zapisywanie…',
  confirming: 'Potwierdzanie…',
  companyRequired: 'Podaj nazwę firmy.',
  companyTooLong: 'Nazwa firmy może mieć najwyżej 120 znaków.',
  emailRequired: 'Podaj adres e-mail.',
  emailInvalid: 'Podaj prawidłowy adres e-mail, np. imie@firma.pl.',
  workEmailRequired: 'Użyj służbowego adresu e-mail. Masz zaproszenie? Otwórz link z zaproszenia.',
  passwordRequired: 'Podaj hasło.',
  passwordTooShort: 'Użyj co najmniej 15 znaków.',
  passwordTooLong: 'Hasło jest za długie.',
  passwordCompromised: 'To hasło występuje w znanych wyciekach danych. Wybierz inne.',
  passwordMismatch: 'Hasła nie są zgodne.',
  resetExpired: 'Ten link jest nieprawidłowy, wygasł lub został już użyty. Poproś o nowy.',
  checkInboxTitle: 'Sprawdź skrzynkę odbiorczą',
  sentTo: 'Wysłaliśmy link na adres',
  signupSentHint: 'Otwórz go na dowolnym urządzeniu, aby dokończyć konfigurację firmy.',
  inviteSentHint: 'Otwórz go na dowolnym urządzeniu, aby dokończyć dołączanie.',
  resetSentTo: 'Jeśli istnieje konto Poneva dla',
  resetSentHint: 'link do resetowania hasła jest już w drodze. Sprawdź też folder ze spamem.',
  resend: 'Wyślij ponownie',
  resendIn: 'Wyślij ponownie za {seconds} s',
  resent: 'Wysłano ponownie. Sprawdź skrzynkę odbiorczą.',
  resendFailed: 'Nie udało się wysłać ponownie. Spróbuj za chwilę.',
  signupExpired: 'Ta rejestracja wygasła. Zacznij od nowa, podając adres e-mail.',
  useAnotherEmail: 'Użyj innego adresu e-mail',
  checkingInvite: 'Sprawdzanie zaproszenia…',
  inviteUnavailableTitle: 'Nie udało się sprawdzić zaproszenia',
  inviteUnavailableLead: 'Sprawdź połączenie i spróbuj ponownie.',
  tryAgain: 'Spróbuj ponownie',
  inviteInvalidTitle: 'Tego zaproszenia nie można użyć',
  inviteInvalidLead:
    'Mogło wygasnąć lub zostać już użyte. Poproś osobę, która Cię zaprosiła, o nowy link.',
  createCompany: 'Utwórz firmę',
  backToInvite: 'Wróć do zaproszenia',
};

const pt: EntryCopy = {
  signupTitle: 'Configure a sua empresa',
  signupLead:
    'Registe-se com o seu e-mail profissional. Torna-se o proprietário e a sua equipa junta-se por convite.',
  loginTitle: 'Iniciar sessão no Poneva',
  inviteTitle: 'Junte-se a {company}',
  forgotTitle: 'Repor a palavra-passe',
  forgotLead: 'Introduza o seu e-mail e enviamos-lhe uma ligação de utilização única.',
  resetTitle: 'Escolha uma nova palavra-passe',
  resetLead: 'A ligação funciona uma vez e expira 30 minutos depois de ser enviada.',
  resetDoneTitle: 'Palavra-passe alterada',
  resetDoneLead: 'A sessão foi terminada em todo o lado. Inicie sessão com a nova palavra-passe.',
  companyLabel: 'Nome da empresa',
  workEmailLabel: 'E-mail profissional',
  emailLabel: 'E-mail',
  passwordLabel: 'Palavra-passe',
  newPasswordLabel: 'Nova palavra-passe',
  confirmPasswordLabel: 'Confirmar nova palavra-passe',
  passwordHelp: 'Pelo menos 15 caracteres',
  signingUpAs: 'A registar-se como {email}',
  forgotPassword: 'Esqueceu-se da palavra-passe?',
  sendResetLink: 'Enviar ligação de reposição',
  saveNewPassword: 'Guardar nova palavra-passe',
  continueToLogin: 'Continuar para iniciar sessão',
  creatingAccount: 'A criar a conta…',
  loggingIn: 'A iniciar sessão…',
  sending: 'A enviar…',
  saving: 'A guardar…',
  confirming: 'A confirmar…',
  companyRequired: 'Introduza o nome da sua empresa.',
  companyTooLong: 'O nome da empresa pode ter no máximo 120 caracteres.',
  emailRequired: 'Introduza o seu endereço de e-mail.',
  emailInvalid: 'Introduza um e-mail válido, como nome@empresa.pt.',
  workEmailRequired:
    'Utilize o seu e-mail profissional. Recebeu um convite? Abra antes a ligação do convite.',
  passwordRequired: 'Introduza a sua palavra-passe.',
  passwordTooShort: 'Utilize pelo menos 15 caracteres.',
  passwordTooLong: 'A palavra-passe é demasiado longa.',
  passwordCompromised: 'Esta palavra-passe aparece em fugas de dados conhecidas. Escolha outra.',
  passwordMismatch: 'As palavras-passe não coincidem.',
  resetExpired: 'Esta ligação é inválida, expirou ou já foi utilizada. Peça uma nova.',
  checkInboxTitle: 'Verifique a sua caixa de entrada',
  sentTo: 'Enviámos uma ligação para',
  signupSentHint: 'Abra-a em qualquer dispositivo para concluir a configuração da sua empresa.',
  inviteSentHint: 'Abra-a em qualquer dispositivo para concluir a adesão.',
  resetSentTo: 'Se existir uma conta Poneva para',
  resetSentHint: 'uma ligação de reposição está a caminho. Verifique também a pasta de spam.',
  resend: 'Reenviar e-mail',
  resendIn: 'Reenviar dentro de {seconds} s',
  resent: 'Reenviado. Verifique a sua caixa de entrada.',
  resendFailed: 'Não foi possível reenviar. Tente novamente daqui a pouco.',
  signupExpired: 'Este registo expirou. Recomece com o seu e-mail.',
  useAnotherEmail: 'Utilizar outro e-mail',
  checkingInvite: 'A verificar o seu convite…',
  inviteUnavailableTitle: 'Não foi possível verificar este convite',
  inviteUnavailableLead: 'Verifique a sua ligação e tente novamente.',
  tryAgain: 'Tentar novamente',
  inviteInvalidTitle: 'Este convite não pode ser utilizado',
  inviteInvalidLead:
    'Pode ter expirado ou já ter sido utilizado. Peça uma nova ligação à pessoa que o convidou.',
  createCompany: 'Criar uma empresa',
  backToInvite: 'Voltar ao seu convite',
};

const ro: EntryCopy = {
  signupTitle: 'Configurați-vă compania',
  signupLead:
    'Înscrieți-vă cu adresa de e-mail de serviciu. Deveniți proprietar, iar colegii se alătură prin invitație.',
  loginTitle: 'Conectați-vă la Poneva',
  inviteTitle: 'Alăturați-vă companiei {company}',
  forgotTitle: 'Resetați parola',
  forgotLead: 'Introduceți adresa de e-mail și vă vom trimite un link de unică folosință.',
  resetTitle: 'Alegeți o parolă nouă',
  resetLead: 'Linkul funcționează o singură dată și expiră la 30 de minute după trimitere.',
  resetDoneTitle: 'Parola a fost schimbată',
  resetDoneLead: 'Ați fost deconectat peste tot. Conectați-vă cu parola nouă.',
  companyLabel: 'Numele companiei',
  workEmailLabel: 'E-mail de serviciu',
  emailLabel: 'E-mail',
  passwordLabel: 'Parolă',
  newPasswordLabel: 'Parolă nouă',
  confirmPasswordLabel: 'Confirmați parola nouă',
  passwordHelp: 'Cel puțin 15 caractere',
  signingUpAs: 'Vă înscrieți ca {email}',
  forgotPassword: 'Ați uitat parola?',
  sendResetLink: 'Trimiteți linkul de resetare',
  saveNewPassword: 'Salvați parola nouă',
  continueToLogin: 'Continuați spre conectare',
  creatingAccount: 'Se creează contul…',
  loggingIn: 'Se face conectarea…',
  sending: 'Se trimite…',
  saving: 'Se salvează…',
  confirming: 'Se confirmă…',
  companyRequired: 'Introduceți numele companiei.',
  companyTooLong: 'Numele companiei poate avea cel mult 120 de caractere.',
  emailRequired: 'Introduceți adresa de e-mail.',
  emailInvalid: 'Introduceți o adresă de e-mail validă, de exemplu nume@companie.ro.',
  workEmailRequired:
    'Folosiți adresa de e-mail de serviciu. Ați primit o invitație? Deschideți linkul din invitație.',
  passwordRequired: 'Introduceți parola.',
  passwordTooShort: 'Folosiți cel puțin 15 caractere.',
  passwordTooLong: 'Parola este prea lungă.',
  passwordCompromised: 'Această parolă apare în scurgeri de date cunoscute. Alegeți alta.',
  passwordMismatch: 'Parolele nu se potrivesc.',
  resetExpired: 'Linkul nu este valid, a expirat sau a fost deja folosit. Solicitați unul nou.',
  checkInboxTitle: 'Verificați-vă căsuța de e-mail',
  sentTo: 'Am trimis un link la',
  signupSentHint: 'Deschideți-l pe orice dispozitiv pentru a finaliza configurarea companiei.',
  inviteSentHint: 'Deschideți-l pe orice dispozitiv pentru a finaliza alăturarea.',
  resetSentTo: 'Dacă există un cont Poneva pentru',
  resetSentHint: 'un link de resetare este pe drum. Verificați și dosarul de spam.',
  resend: 'Retrimiteți e-mailul',
  resendIn: 'Retrimitere în {seconds} s',
  resent: 'Trimis din nou. Verificați-vă căsuța de e-mail.',
  resendFailed: 'Nu l-am putut retrimite. Încercați din nou peste puțin timp.',
  signupExpired: 'Această înscriere a expirat. Începeți din nou cu adresa de e-mail.',
  useAnotherEmail: 'Folosiți alt e-mail',
  checkingInvite: 'Se verifică invitația…',
  inviteUnavailableTitle: 'Nu am putut verifica această invitație',
  inviteUnavailableLead: 'Verificați conexiunea și încercați din nou.',
  tryAgain: 'Încercați din nou',
  inviteInvalidTitle: 'Această invitație nu poate fi folosită',
  inviteInvalidLead:
    'Este posibil să fi expirat sau să fi fost deja folosită. Cereți un link nou persoanei care v-a invitat.',
  createCompany: 'Creați o companie',
  backToInvite: 'Înapoi la invitație',
};

const sk: EntryCopy = {
  signupTitle: 'Nastavte svoju firmu',
  signupLead:
    'Zaregistrujte sa pracovným e-mailom. Stanete sa vlastníkom a kolegovia sa pridajú na pozvánku.',
  loginTitle: 'Prihlásiť sa do Poneva',
  inviteTitle: 'Pridajte sa k {company}',
  forgotTitle: 'Obnovte heslo',
  forgotLead: 'Zadajte svoj e-mail a pošleme vám jednorazový odkaz.',
  resetTitle: 'Zvoľte nové heslo',
  resetLead: 'Odkaz funguje raz a vyprší 30 minút po odoslaní.',
  resetDoneTitle: 'Heslo bolo zmenené',
  resetDoneLead: 'Boli ste všade odhlásení. Prihláste sa novým heslom.',
  companyLabel: 'Názov firmy',
  workEmailLabel: 'Pracovný e-mail',
  emailLabel: 'E-mail',
  passwordLabel: 'Heslo',
  newPasswordLabel: 'Nové heslo',
  confirmPasswordLabel: 'Potvrďte nové heslo',
  passwordHelp: 'Aspoň 15 znakov',
  signingUpAs: 'Registrujete sa ako {email}',
  forgotPassword: 'Zabudli ste heslo?',
  sendResetLink: 'Odoslať odkaz na obnovenie',
  saveNewPassword: 'Uložiť nové heslo',
  continueToLogin: 'Pokračovať na prihlásenie',
  creatingAccount: 'Vytvára sa účet…',
  loggingIn: 'Prihlasovanie…',
  sending: 'Odosiela sa…',
  saving: 'Ukladá sa…',
  confirming: 'Potvrdzuje sa…',
  companyRequired: 'Zadajte názov firmy.',
  companyTooLong: 'Názov firmy môže mať najviac 120 znakov.',
  emailRequired: 'Zadajte svoju e-mailovú adresu.',
  emailInvalid: 'Zadajte platnú e-mailovú adresu, napríklad meno@firma.sk.',
  workEmailRequired: 'Použite pracovný e-mail. Máte pozvánku? Otvorte radšej odkaz z pozvánky.',
  passwordRequired: 'Zadajte heslo.',
  passwordTooShort: 'Použite aspoň 15 znakov.',
  passwordTooLong: 'Heslo je príliš dlhé.',
  passwordCompromised: 'Toto heslo sa objavuje v známych únikoch údajov. Zvoľte iné.',
  passwordMismatch: 'Heslá sa nezhodujú.',
  resetExpired: 'Tento odkaz je neplatný, vypršal alebo už bol použitý. Požiadajte o nový.',
  checkInboxTitle: 'Skontrolujte si schránku',
  sentTo: 'Poslali sme odkaz na adresu',
  signupSentHint: 'Otvorte ho na ľubovoľnom zariadení a dokončite nastavenie firmy.',
  inviteSentHint: 'Otvorte ho na ľubovoľnom zariadení a dokončite pridanie.',
  resetSentTo: 'Ak existuje účet Poneva pre',
  resetSentHint: 'odkaz na obnovenie je na ceste. Skontrolujte aj priečinok so spamom.',
  resend: 'Odoslať znova',
  resendIn: 'Odoslať znova o {seconds} s',
  resent: 'Odoslané znova. Skontrolujte si schránku.',
  resendFailed: 'Nepodarilo sa odoslať znova. Skúste to o chvíľu.',
  signupExpired: 'Táto registrácia vypršala. Začnite znova so svojím e-mailom.',
  useAnotherEmail: 'Použiť iný e-mail',
  checkingInvite: 'Kontrolujeme vašu pozvánku…',
  inviteUnavailableTitle: 'Pozvánku sa nepodarilo skontrolovať',
  inviteUnavailableLead: 'Skontrolujte pripojenie a skúste to znova.',
  tryAgain: 'Skúsiť znova',
  inviteInvalidTitle: 'Túto pozvánku nemožno použiť',
  inviteInvalidLead:
    'Mohla vypršať alebo už bola použitá. Požiadajte človeka, ktorý vás pozval, o nový odkaz.',
  createCompany: 'Vytvoriť firmu',
  backToInvite: 'Späť na pozvánku',
};

const sl: EntryCopy = {
  signupTitle: 'Nastavite svoje podjetje',
  signupLead:
    'Registrirajte se s službenim e-poštnim naslovom. Postanete lastnik, sodelavci pa se pridružijo s povabilom.',
  loginTitle: 'Prijava v Poneva',
  inviteTitle: 'Pridružite se {company}',
  forgotTitle: 'Ponastavite geslo',
  forgotLead: 'Vnesite svoj e-poštni naslov in poslali vam bomo povezavo za enkratno uporabo.',
  resetTitle: 'Izberite novo geslo',
  resetLead: 'Povezava deluje enkrat in poteče 30 minut po pošiljanju.',
  resetDoneTitle: 'Geslo je spremenjeno',
  resetDoneLead: 'Povsod ste bili odjavljeni. Prijavite se z novim geslom.',
  companyLabel: 'Ime podjetja',
  workEmailLabel: 'Službeni e-poštni naslov',
  emailLabel: 'E-pošta',
  passwordLabel: 'Geslo',
  newPasswordLabel: 'Novo geslo',
  confirmPasswordLabel: 'Potrdite novo geslo',
  passwordHelp: 'Vsaj 15 znakov',
  signingUpAs: 'Registrirate se kot {email}',
  forgotPassword: 'Ste pozabili geslo?',
  sendResetLink: 'Pošlji povezavo za ponastavitev',
  saveNewPassword: 'Shrani novo geslo',
  continueToLogin: 'Nadaljuj na prijavo',
  creatingAccount: 'Ustvarjanje računa…',
  loggingIn: 'Prijavljanje…',
  sending: 'Pošiljanje…',
  saving: 'Shranjevanje…',
  confirming: 'Potrjevanje…',
  companyRequired: 'Vnesite ime podjetja.',
  companyTooLong: 'Ime podjetja ima lahko največ 120 znakov.',
  emailRequired: 'Vnesite svoj e-poštni naslov.',
  emailInvalid: 'Vnesite veljaven e-poštni naslov, na primer ime@podjetje.si.',
  workEmailRequired:
    'Uporabite službeni e-poštni naslov. Ste prejeli povabilo? Raje odprite povezavo iz povabila.',
  passwordRequired: 'Vnesite geslo.',
  passwordTooShort: 'Uporabite vsaj 15 znakov.',
  passwordTooLong: 'Geslo je predolgo.',
  passwordCompromised: 'To geslo se pojavlja v znanih uhajanjih podatkov. Izberite drugo.',
  passwordMismatch: 'Gesli se ne ujemata.',
  resetExpired: 'Ta povezava ni veljavna, je potekla ali je bila že uporabljena. Zahtevajte novo.',
  checkInboxTitle: 'Preverite svoj nabiralnik',
  sentTo: 'Povezavo smo poslali na',
  signupSentHint: 'Odprite jo v kateri koli napravi, da dokončate nastavitev podjetja.',
  inviteSentHint: 'Odprite jo v kateri koli napravi, da dokončate pridružitev.',
  resetSentTo: 'Če za naslov',
  resetSentHint:
    'obstaja račun Poneva, je povezava za ponastavitev že na poti. Preverite tudi mapo z neželeno pošto.',
  resend: 'Pošlji znova',
  resendIn: 'Pošlji znova čez {seconds} s',
  resent: 'Poslano znova. Preverite svoj nabiralnik.',
  resendFailed: 'Ponovno pošiljanje ni uspelo. Poskusite znova čez trenutek.',
  signupExpired: 'Ta registracija je potekla. Začnite znova s svojim e-poštnim naslovom.',
  useAnotherEmail: 'Uporabi drug e-poštni naslov',
  checkingInvite: 'Preverjanje povabila…',
  inviteUnavailableTitle: 'Povabila ni bilo mogoče preveriti',
  inviteUnavailableLead: 'Preverite povezavo in poskusite znova.',
  tryAgain: 'Poskusi znova',
  inviteInvalidTitle: 'Tega povabila ni mogoče uporabiti',
  inviteInvalidLead:
    'Morda je poteklo ali je bilo že uporabljeno. Osebo, ki vas je povabila, prosite za novo povezavo.',
  createCompany: 'Ustvari podjetje',
  backToInvite: 'Nazaj na povabilo',
};

const es: EntryCopy = {
  signupTitle: 'Configura tu empresa',
  signupLead:
    'Regístrate con tu correo de trabajo. Serás el propietario y tu equipo se unirá por invitación.',
  loginTitle: 'Iniciar sesión en Poneva',
  inviteTitle: 'Únete a {company}',
  forgotTitle: 'Restablece tu contraseña',
  forgotLead: 'Introduce tu correo y te enviaremos un enlace de un solo uso.',
  resetTitle: 'Elige una contraseña nueva',
  resetLead: 'El enlace funciona una vez y caduca 30 minutos después de enviarse.',
  resetDoneTitle: 'Contraseña cambiada',
  resetDoneLead: 'Se ha cerrado tu sesión en todas partes. Inicia sesión con tu contraseña nueva.',
  companyLabel: 'Nombre de la empresa',
  workEmailLabel: 'Correo de trabajo',
  emailLabel: 'Correo electrónico',
  passwordLabel: 'Contraseña',
  newPasswordLabel: 'Contraseña nueva',
  confirmPasswordLabel: 'Confirma la contraseña nueva',
  passwordHelp: 'Al menos 15 caracteres',
  signingUpAs: 'Registrándote como {email}',
  forgotPassword: '¿Has olvidado la contraseña?',
  sendResetLink: 'Enviar enlace de restablecimiento',
  saveNewPassword: 'Guardar contraseña nueva',
  continueToLogin: 'Continuar al inicio de sesión',
  creatingAccount: 'Creando la cuenta…',
  loggingIn: 'Iniciando sesión…',
  sending: 'Enviando…',
  saving: 'Guardando…',
  confirming: 'Confirmando…',
  companyRequired: 'Introduce el nombre de tu empresa.',
  companyTooLong: 'El nombre de la empresa no puede superar los 120 caracteres.',
  emailRequired: 'Introduce tu correo electrónico.',
  emailInvalid: 'Introduce un correo válido, como nombre@empresa.es.',
  workEmailRequired: 'Usa tu correo de trabajo. ¿Te han invitado? Abre tu enlace de invitación.',
  passwordRequired: 'Introduce tu contraseña.',
  passwordTooShort: 'Usa al menos 15 caracteres.',
  passwordTooLong: 'La contraseña es demasiado larga.',
  passwordCompromised: 'Esta contraseña aparece en filtraciones de datos conocidas. Elige otra.',
  passwordMismatch: 'Las contraseñas no coinciden.',
  resetExpired: 'Este enlace no es válido, ha caducado o ya se ha usado. Solicita uno nuevo.',
  checkInboxTitle: 'Revisa tu bandeja de entrada',
  sentTo: 'Hemos enviado un enlace a',
  signupSentHint: 'Ábrelo en cualquier dispositivo para terminar de configurar tu empresa.',
  inviteSentHint: 'Ábrelo en cualquier dispositivo para terminar de unirte.',
  resetSentTo: 'Si existe una cuenta de Poneva para',
  resetSentHint: 'hay un enlace de restablecimiento en camino. Revisa también la carpeta de spam.',
  resend: 'Reenviar correo',
  resendIn: 'Reenviar en {seconds} s',
  resent: 'Reenviado. Revisa tu bandeja de entrada.',
  resendFailed: 'No hemos podido reenviarlo. Inténtalo de nuevo en un momento.',
  signupExpired: 'Este registro ha caducado. Vuelve a empezar con tu correo.',
  useAnotherEmail: 'Usar otro correo',
  checkingInvite: 'Comprobando tu invitación…',
  inviteUnavailableTitle: 'No hemos podido comprobar esta invitación',
  inviteUnavailableLead: 'Comprueba tu conexión e inténtalo de nuevo.',
  tryAgain: 'Reintentar',
  inviteInvalidTitle: 'Esta invitación no se puede usar',
  inviteInvalidLead:
    'Puede que haya caducado o que ya se haya usado. Pide un enlace nuevo a la persona que te invitó.',
  createCompany: 'Crear una empresa',
  backToInvite: 'Volver a tu invitación',
};

const sv: EntryCopy = {
  signupTitle: 'Kom igång med ditt företag',
  signupLead:
    'Registrera dig med din e-postadress på jobbet. Du blir ägare, och kollegor ansluter via inbjudan.',
  loginTitle: 'Logga in på Poneva',
  inviteTitle: 'Gå med i {company}',
  forgotTitle: 'Återställ ditt lösenord',
  forgotLead: 'Ange din e-postadress så skickar vi en engångslänk.',
  resetTitle: 'Välj ett nytt lösenord',
  resetLead: 'Länken fungerar en gång och slutar gälla 30 minuter efter att den skickades.',
  resetDoneTitle: 'Lösenordet är ändrat',
  resetDoneLead: 'Du har loggats ut överallt. Logga in med ditt nya lösenord.',
  companyLabel: 'Företagets namn',
  workEmailLabel: 'E-postadress på jobbet',
  emailLabel: 'E-post',
  passwordLabel: 'Lösenord',
  newPasswordLabel: 'Nytt lösenord',
  confirmPasswordLabel: 'Bekräfta nytt lösenord',
  passwordHelp: 'Minst 15 tecken',
  signingUpAs: 'Du registrerar dig som {email}',
  forgotPassword: 'Glömt lösenordet?',
  sendResetLink: 'Skicka återställningslänk',
  saveNewPassword: 'Spara nytt lösenord',
  continueToLogin: 'Fortsätt till inloggning',
  creatingAccount: 'Skapar konto…',
  loggingIn: 'Loggar in…',
  sending: 'Skickar…',
  saving: 'Sparar…',
  confirming: 'Bekräftar…',
  companyRequired: 'Ange företagets namn.',
  companyTooLong: 'Företagsnamnet får vara högst 120 tecken.',
  emailRequired: 'Ange din e-postadress.',
  emailInvalid: 'Ange en giltig e-postadress, till exempel namn@foretag.se.',
  workEmailRequired:
    'Använd din e-postadress på jobbet. Har du fått en inbjudan? Öppna inbjudningslänken i stället.',
  passwordRequired: 'Ange ditt lösenord.',
  passwordTooShort: 'Använd minst 15 tecken.',
  passwordTooLong: 'Lösenordet är för långt.',
  passwordCompromised: 'Lösenordet finns i kända dataläckor. Välj ett annat.',
  passwordMismatch: 'Lösenorden matchar inte.',
  resetExpired: 'Länken är ogiltig, har gått ut eller har redan använts. Begär en ny.',
  checkInboxTitle: 'Kolla din inkorg',
  sentTo: 'Vi skickade en länk till',
  signupSentHint: 'Öppna den på valfri enhet för att bli klar med ditt företag.',
  inviteSentHint: 'Öppna den på valfri enhet för att gå med.',
  resetSentTo: 'Om det finns ett Poneva-konto för',
  resetSentHint: 'är en återställningslänk på väg. Kolla även skräpposten.',
  resend: 'Skicka igen',
  resendIn: 'Skicka igen om {seconds} s',
  resent: 'Skickat igen. Kolla din inkorg.',
  resendFailed: 'Vi kunde inte skicka igen. Försök om en stund.',
  signupExpired: 'Registreringen har gått ut. Börja om med din e-postadress.',
  useAnotherEmail: 'Använd en annan e-postadress',
  checkingInvite: 'Kontrollerar din inbjudan…',
  inviteUnavailableTitle: 'Vi kunde inte kontrollera inbjudan',
  inviteUnavailableLead: 'Kontrollera din anslutning och försök igen.',
  tryAgain: 'Försök igen',
  inviteInvalidTitle: 'Inbjudan kan inte användas',
  inviteInvalidLead:
    'Den kan ha gått ut eller redan använts. Be personen som bjöd in dig om en ny länk.',
  createCompany: 'Skapa ett företag',
  backToInvite: 'Tillbaka till din inbjudan',
};

const entryCopy: NamespaceCatalog<EntryCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default entryCopy;
